import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { 
  Database, 
  Download, 
  Trash2, 
  Eye, 
  Lock,
  Shield,
  FileText,
  Users,
  Calendar,
  Search,
  Filter,
  AlertTriangle,
  CheckCircle
} from 'lucide-react'
import { useStore } from '../../store/useStore'
import GlassCard from '../../components/ui/GlassCard'
import Button from '../../components/ui/Button'
import Input from '../../components/ui/Input'
import AdminLayout from '../../components/admin/AdminLayout'
import AuditLogger from '../../utils/auditLogger'

const DataManagement = () => {
  const { applications, galleryImages, banners, jobPostings } = useStore()
  const [selectedDataType, setSelectedDataType] = useState('applications')
  const [searchTerm, setSearchTerm] = useState('')
  const [dateFilter, setDateFilter] = useState('')
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [auditLogs, setAuditLogs] = useState([])

  useEffect(() => {
    // Charger les logs d'audit récents
    const logs = AuditLogger.getLogs({ limit: 50 })
    setAuditLogs(logs)
  }, [])

  const dataTypes = [
    { 
      id: 'applications', 
      label: 'Candidatures', 
      icon: Users, 
      count: applications.length,
      description: 'Données des candidats aux offres d\'emploi'
    },
    { 
      id: 'gallery', 
      label: 'Médias', 
      icon: FileText, 
      count: Object.values(galleryImages).flat().length,
      description: 'Images et vidéos des galeries'
    },
    { 
      id: 'banners', 
      label: 'Bannières', 
      icon: Database, 
      count: banners.length,
      description: 'Bannières événementielles'
    },
    { 
      id: 'jobs', 
      label: 'Offres d\'emploi', 
      icon: Calendar, 
      count: jobPostings.length,
      description: 'Offres d\'emploi publiées'
    }
  ]

  const handleExportData = async (dataType) => {
    try {
      let data = []
      let filename = ''
      
      switch (dataType) {
        case 'applications':
          data = applications
          filename = 'candidatures'
          break
        case 'gallery':
          data = Object.values(galleryImages).flat()
          filename = 'medias_galerie'
          break
        case 'banners':
          data = banners
          filename = 'bannieres'
          break
        case 'jobs':
          data = jobPostings
          filename = 'offres_emploi'
          break
      }

      // Filtrer les données si nécessaire
      if (searchTerm) {
        data = data.filter(item => 
          JSON.stringify(item).toLowerCase().includes(searchTerm.toLowerCase())
        )
      }

      if (dateFilter) {
        data = data.filter(item => {
          const itemDate = new Date(item.date || item.applicationDate || item.publicationDate)
          const filterDate = new Date(dateFilter)
          return itemDate >= filterDate
        })
      }

      const exportData = {
        exportDate: new Date().toISOString(),
        dataType,
        recordCount: data.length,
        data
      }

      const blob = new Blob([JSON.stringify(exportData, null, 2)], { 
        type: 'application/json' 
      })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${filename}_export_${new Date().toISOString().split('T')[0]}.json`
      a.click()
      URL.revokeObjectURL(url)

      // Logger l'export
      await AuditLogger.log({
        action: 'DATA_EXPORT',
        user: 'admin',
        severity: 'INFO',
        details: { dataType, recordCount: data.length }
      })

      alert(`Export réussi : ${data.length} enregistrements`)
    } catch (error) {
      console.error('Erreur lors de l\'export:', error)
      alert('Erreur lors de l\'export des données')
    }
  }

  const handleDeleteAllData = async (dataType) => {
    if (!showDeleteConfirm) {
      setShowDeleteConfirm(true)
      return
    }

    try {
      // En production, implémenter la vraie suppression
      console.log(`Suppression de toutes les données: ${dataType}`)
      
      // Logger la suppression
      await AuditLogger.log({
        action: 'DATA_DELETE_ALL',
        user: 'admin',
        severity: 'WARNING',
        details: { dataType }
      })

      alert(`Toutes les données ${dataType} ont été supprimées`)
      setShowDeleteConfirm(false)
    } catch (error) {
      console.error('Erreur lors de la suppression:', error)
      alert('Erreur lors de la suppression des données')
    }
  }

  const getDataPreview = (dataType) => {
    let data = []
    
    switch (dataType) {
      case 'applications':
        data = applications.slice(0, 5)
        break
      case 'gallery':
        data = Object.values(galleryImages).flat().slice(0, 5)
        break
      case 'banners':
        data = banners.slice(0, 5)
        break
      case 'jobs':
        data = jobPostings.slice(0, 5)
        break
    }

    return data
  }

  const getStats = () => {
    const stats = AuditLogger.getStats()
    return stats
  }

  return (
    <AdminLayout>
      <div className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <div className="flex items-center space-x-3 mb-4">
              <Shield className="w-8 h-8 text-primary-600" />
              <h1 className="text-3xl font-display font-bold gradient-text">
                Gestion des Données Personnelles
              </h1>
            </div>
            <p className="text-gray-600">
              Conformité RGPD - Gestion, export et suppression des données
            </p>
          </motion.div>

          {/* Statistiques */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-8"
          >
            <GlassCard className="p-6">
              <h2 className="text-xl font-bold text-black mb-4">Statistiques des Données</h2>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {dataTypes.map((type) => {
                  const Icon = type.icon
                  return (
                    <div key={type.id} className="text-center p-4 bg-gray-50 rounded-lg">
                      <Icon className="w-8 h-8 text-primary-600 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-black">{type.count}</p>
                      <p className="text-sm text-gray-600">{type.label}</p>
                    </div>
                  )
                })}
              </div>
            </GlassCard>
          </motion.div>

          {/* Types de données */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Liste des types */}
            <div className="lg:col-span-1">
              <GlassCard className="p-6">
                <h2 className="text-xl font-bold text-black mb-4">Types de Données</h2>
                <div className="space-y-3">
                  {dataTypes.map((type) => {
                    const Icon = type.icon
                    const isSelected = selectedDataType === type.id
                    
                    return (
                      <button
                        key={type.id}
                        onClick={() => setSelectedDataType(type.id)}
                        className={`w-full p-4 rounded-lg text-left transition-colors ${
                          isSelected 
                            ? 'bg-primary-100 border-2 border-primary-500' 
                            : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'
                        }`}
                      >
                        <div className="flex items-center space-x-3 mb-2">
                          <Icon className="w-5 h-5 text-primary-600" />
                          <h3 className="font-semibold text-black">{type.label}</h3>
                          <span className="ml-auto bg-primary-600 text-white px-2 py-1 rounded-full text-xs">
                            {type.count}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600">{type.description}</p>
                      </button>
                    )
                  })}
                </div>
              </GlassCard>
            </div>

            {/* Détails et actions */}
            <div className="lg:col-span-2">
              <GlassCard className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-black">
                    {dataTypes.find(t => t.id === selectedDataType)?.label}
                  </h2>
                  <div className="flex space-x-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      icon={Eye}
                      onClick={() => setSelectedDataType(selectedDataType)}
                    >
                      Voir
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      icon={Download}
                      onClick={() => handleExportData(selectedDataType)}
                    >
                      Exporter
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      icon={Trash2}
                      onClick={() => handleDeleteAllData(selectedDataType)}
                      className="text-red-600 hover:text-red-700"
                    >
                      Supprimer
                    </Button>
                  </div>
                </div>

                {/* Filtres */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <Input
                    label="Recherche"
                    placeholder="Rechercher dans les données..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    icon={Search}
                  />
                  <Input
                    label="Filtrer par date"
                    type="date"
                    value={dateFilter}
                    onChange={(e) => setDateFilter(e.target.value)}
                    icon={Calendar}
                  />
                </div>

                {/* Aperçu des données */}
                <div className="mb-6">
                  <h3 className="font-semibold text-black mb-3">Aperçu des données</h3>
                  <div className="bg-gray-50 rounded-lg p-4 max-h-64 overflow-y-auto">
                    <pre className="text-xs text-gray-700 whitespace-pre-wrap">
                      {JSON.stringify(getDataPreview(selectedDataType), null, 2)}
                    </pre>
                  </div>
                </div>

                {/* Confirmation de suppression */}
                {showDeleteConfirm && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg"
                  >
                    <div className="flex items-center space-x-2 mb-2">
                      <AlertTriangle className="w-5 h-5 text-red-600" />
                      <h4 className="font-semibold text-red-800">Confirmation de suppression</h4>
                    </div>
                    <p className="text-red-700 text-sm mb-3">
                      Êtes-vous sûr de vouloir supprimer TOUTES les données de type "{selectedDataType}" ? 
                      Cette action est irréversible.
                    </p>
                    <div className="flex space-x-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setShowDeleteConfirm(false)}
                      >
                        Annuler
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleDeleteAllData(selectedDataType)}
                        className="bg-red-600 text-white hover:bg-red-700"
                      >
                        Confirmer la suppression
                      </Button>
                    </div>
                  </motion.div>
                )}
              </GlassCard>
            </div>
          </div>

          {/* Politique de confidentialité */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mt-8"
          >
            <GlassCard className="p-6">
              <h2 className="text-xl font-bold text-black mb-4">Politique de Confidentialité</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <h3 className="font-semibold text-black mb-2">Collecte de données</h3>
                    <p className="text-gray-600 text-sm">
                      Nous collectons uniquement les données nécessaires au fonctionnement 
                      de nos services d'accompagnement pour personnes en déficience intellectuelle.
                    </p>
                  </div>
                  
                  <div>
                    <h3 className="font-semibold text-black mb-2">Utilisation</h3>
                    <p className="text-gray-600 text-sm">
                      Les données sont utilisées pour la gestion des résidents, des candidatures 
                      et la communication avec les familles.
                    </p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <h3 className="font-semibold text-black mb-2">Protection</h3>
                    <p className="text-gray-600 text-sm">
                      Toutes les données sensibles sont chiffrées et stockées de manière sécurisée 
                      conformément aux standards de sécurité.
                    </p>
                  </div>
                  
                  <div>
                    <h3 className="font-semibold text-black mb-2">Droits des utilisateurs</h3>
                    <p className="text-gray-600 text-sm">
                      Vous avez le droit d'accès, de rectification, de suppression et de portabilité 
                      de vos données conformément au RGPD.
                    </p>
                  </div>
                </div>
              </div>
            </GlassCard>
          </motion.div>
        </div>
      </div>
    </AdminLayout>
  )
}

export default DataManagement
